/**
 * Shared helpers for restoring scroll position across client-side navigations.
 *
 * Two distinct needs, both driven by the same underlying problem: the store page's
 * results grid renders progressively (react-instantsearch + IntersectionObserver), so
 * the document can be far shorter than its final height right after a navigation or a
 * filter change. A naive `window.scrollTo` fired at that moment either gets clamped by
 * the browser to a too-short document, or computes a target based on a not-yet-settled
 * layout. Both helpers below poll briefly until the page has stopped moving/growing
 * before finalizing the scroll, instead of trusting a single synchronous measurement.
 *
 *  - saveScrollPosition/restoreScrollPosition: restore an absolute Y offset, keyed by
 *    history location key (browser back/forward - RAS-2549).
 *  - scrollToElementWhenStable: keep re-aligning an element under the sticky header
 *    until its position stops shifting (in-page filter changes - RAS-2550).
 *  - saveVisibleCount/getSavedVisibleCount: remember how many progressively-loaded
 *    cards were revealed, keyed the same way, so a restored page doesn't start back at
 *    the first page of results.
 *
 * Keying note: react-router v5's `history` package leaves `location.key` undefined for
 * the very first entry in a browser tab (a real navigation, not a push/replace) - it's
 * only assigned from the first client-side navigation onward. Landing directly on a page
 * (shared link, fresh tab) is exactly the common case this feature needs to handle, so
 * getRestorationKey falls back to `pathname+search` whenever `key` is missing. There is
 * only ever one such keyless entry per tab, and it always maps to the same URL, so the
 * fallback is stable across repeated back/forward to that same initial entry.
 */

export const getRestorationKey = location => {
  if (!location) return null;
  return location.key || `${location.pathname || ''}${location.search || ''}`;
};

const SCROLL_POS_PREFIX = 'scrollPos:';
const VISIBLE_COUNT_PREFIX = 'storeHitsCount:';
const MAX_ENTRIES_PER_PREFIX = 50;

const getStorage = () => {
  try {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;
    return window.sessionStorage;
  } catch (e) {
    return null;
  }
};

// sessionStorage has no built-in eviction; cap each namespace so a long session of
// repeated filter/search changes (each gets its own history key) can't grow unbounded.
const capEntries = (storage, prefix) => {
  try {
    const keys = [];
    for (let i = 0; i < storage.length; i += 1) {
      const k = storage.key(i);
      if (k && k.indexOf(prefix) === 0) keys.push(k);
    }
    const excess = keys.length - MAX_ENTRIES_PER_PREFIX;
    for (let i = 0; i < excess; i += 1) {
      storage.removeItem(keys[i]);
    }
  } catch (e) {
    // Non-critical bookkeeping; ignore failures.
  }
};

const setItem = (prefix, key, value) => {
  const storage = getStorage();
  if (!storage || !key) return;
  try {
    storage.setItem(`${prefix}${key}`, String(value));
    capEntries(storage, prefix);
  } catch (e) {
    // Storage full or disabled (e.g. Safari private mode edge cases) - skip silently.
  }
};

const getItem = (prefix, key) => {
  const storage = getStorage();
  if (!storage || !key) return null;
  try {
    return storage.getItem(`${prefix}${key}`);
  } catch (e) {
    return null;
  }
};

export const saveScrollPosition = (key, y) => setItem(SCROLL_POS_PREFIX, key, y);

const getSavedScrollY = key => {
  const raw = getItem(SCROLL_POS_PREFIX, key);
  if (raw == null) return null;
  const y = parseInt(raw, 10);
  return Number.isNaN(y) ? null : y;
};

/**
 * Polls until the document is tall enough to reach targetY (or times out), then scrolls.
 */
const scrollToYWhenReady = (targetY, { maxAttempts = 40, intervalMs = 50 } = {}) => {
  if (typeof window === 'undefined' || typeof targetY !== 'number') return;
  let attempts = 0;

  const tryScroll = () => {
    attempts += 1;
    const maxScrollable = Math.max(
      0,
      document.documentElement.scrollHeight - window.innerHeight
    );
    if (maxScrollable >= targetY || attempts >= maxAttempts) {
      // `behavior: 'instant'` is required here, not just omitting the option - this repo
      // sets `scroll-behavior: smooth` on the scrolling element (StoreProfilePage.module.css),
      // and CSSOM's 'auto' (the default) defers to that, silently turning every restore
      // into a ~1-1.5s animated scroll instead of landing immediately.
      window.scrollTo({ top: Math.min(targetY, maxScrollable), left: 0, behavior: 'instant' });
      return;
    }
    window.setTimeout(tryScroll, intervalMs);
  };

  requestAnimationFrame(tryScroll);
};

export const restoreScrollPosition = key => {
  const savedY = getSavedScrollY(key);
  if (savedY == null) return;
  scrollToYWhenReady(savedY);
};

/**
 * Keeps an element aligned `offsetFromTop` px below the viewport top, re-measuring
 * until its position stops shifting between checks (or a timeout is hit). This absorbs
 * the transient layout collapse that happens while a filtered result set reloads.
 */
export const scrollToElementWhenStable = (
  getElement,
  offsetFromTop,
  { maxAttempts = 40, intervalMs = 50, stableChecks = 2 } = {}
) => {
  if (typeof window === 'undefined') return;
  let attempts = 0;
  let stableCount = 0;
  let lastTop = null;

  const tryScroll = () => {
    attempts += 1;
    const el = getElement();
    if (!el) {
      if (attempts < maxAttempts) window.setTimeout(tryScroll, intervalMs);
      return;
    }

    const rectTop = el.getBoundingClientRect().top;
    const currentScrollY = window.scrollY ?? window.pageYOffset ?? 0;
    const targetTop = Math.max(0, rectTop + currentScrollY - offsetFromTop);
    // 'instant', not 'auto' - see the note in scrollToYWhenReady above.
    window.scrollTo({ top: targetTop, left: 0, behavior: 'instant' });

    stableCount = lastTop !== null && Math.abs(rectTop - lastTop) < 1 ? stableCount + 1 : 0;
    lastTop = rectTop;

    if (stableCount >= stableChecks || attempts >= maxAttempts) return;
    window.setTimeout(tryScroll, intervalMs);
  };

  requestAnimationFrame(tryScroll);
};

export const saveVisibleCount = (key, count) => setItem(VISIBLE_COUNT_PREFIX, key, count);

export const getSavedVisibleCount = key => {
  const raw = getItem(VISIBLE_COUNT_PREFIX, key);
  if (raw == null) return null;
  const count = parseInt(raw, 10);
  return Number.isNaN(count) ? null : count;
};
